import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const API_BASE_URL = `${API_URL}${API_BASE_PATH}/alarm`
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

const apiClient = axios.create()

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(JWT_STORAGE_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const alarmApi = {
  // Alarms
  getAlarms: (params = {}) => apiClient.get(`${API_BASE_URL}/alarm/`, { params }),
  getAlarm: (id) => apiClient.get(`${API_BASE_URL}/alarm/${id}/`),
  createAlarm: (data) => apiClient.post(`${API_BASE_URL}/alarm/`, data),
  updateAlarm: (id, data) => apiClient.put(`${API_BASE_URL}/alarm/${id}/`, data),
  partialUpdateAlarm: (id, data) => apiClient.patch(`${API_BASE_URL}/alarm/${id}/`, data),
  deleteAlarm: (id) => apiClient.delete(`${API_BASE_URL}/alarm/${id}/`),
  // Legacy-compatible Event Monitor create/update (server builds condition_tree like Symfony)
  createEventMonitor: (data) => apiClient.post(`${API_BASE_URL}/event-monitor/`, data),
  updateEventMonitor: (id, data) => apiClient.put(`${API_BASE_URL}/event-monitor/${id}/`, data),
  testEventMonitor: (data) => apiClient.post(`${API_BASE_URL}/event-monitor/test/`, data),

  // ── Row-level Event Monitor actions (Acknowledge / Pause / Resume) ──
  // Implemented as PATCHes against the existing alarm endpoint; no
  // backend changes required because the AlarmSerializer exposes
  // `fields = '__all__'` so acknowledged_time and enabled are writable.
  // acknowledged_by is intentionally omitted — the server can backfill
  // it from request.user where it cares; we only need to flag the row
  // as acknowledged client-side so the Status column reflects the change.
  acknowledgeAlarm: (id) => apiClient.patch(`${API_BASE_URL}/alarm/${id}/`, {
    acknowledged_time: new Date().toISOString(),
  }),
  // Pause / resume — `enabled` is an IntegerField in the legacy schema
  // (0 / 1), not a boolean, so we send numbers.
  pauseAlarm:  (id) => apiClient.patch(`${API_BASE_URL}/alarm/${id}/`, { enabled: 0 }),
  resumeAlarm: (id) => apiClient.patch(`${API_BASE_URL}/alarm/${id}/`, { enabled: 1 }),

  // Alarm Actions
  getAlarmActions: (alarmId) => apiClient.get(`${API_BASE_URL}/alarmaction/`, { params: { alarm: alarmId } }),
  createAlarmAction: (data) => apiClient.post(`${API_BASE_URL}/alarmaction/`, data),
  updateAlarmAction: (id, data) => apiClient.put(`${API_BASE_URL}/alarmaction/${id}/`, data),
  deleteAlarmAction: (id) => apiClient.delete(`${API_BASE_URL}/alarmaction/${id}/`),

  // Alarm Parameters
  getAlarmParameters: (alarmId) => apiClient.get(`${API_BASE_URL}/alarmparameter/`, { params: { alarm: alarmId } }),
  createAlarmParameter: (data) => apiClient.post(`${API_BASE_URL}/alarmparameter/`, data),
  updateAlarmParameter: (id, data) => apiClient.put(`${API_BASE_URL}/alarmparameter/${id}/`, data),
  deleteAlarmParameter: (id) => apiClient.delete(`${API_BASE_URL}/alarmparameter/${id}/`),

  // Recipient Groups
  getRecipientGroups: (projectId) => apiClient.get(`${API_BASE_URL}/alarmrecipientgroup/`, { params: { project: projectId } }),
  getRecipientGroup: (id) => apiClient.get(`${API_BASE_URL}/alarmrecipientgroup/${id}/`),
  createRecipientGroup: (data) => apiClient.post(`${API_BASE_URL}/alarmrecipientgroup/`, data),
  updateRecipientGroup: (id, data) => apiClient.put(`${API_BASE_URL}/alarmrecipientgroup/${id}/`, data),
  deleteRecipientGroup: (id) => apiClient.delete(`${API_BASE_URL}/alarmrecipientgroup/${id}/`),

  // Alarm Reports (history/events)
  getAlarmReports: (alarmId) => apiClient.get(`${API_BASE_URL}/alarmreport/`, { params: { alarm: alarmId } }),
}

export default alarmApi
